<template>
  <HeaderBar />

  <section class="carvia-container">
    <div class="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
      <DateRangeFilterPicker
        v-model:start-date="startDate"
        v-model:end-date="endDate"
        :placeholder="t('dateRange')"
      />
      <Select
        v-model="status"
        :placeholder="t('statusLabel')"
        :options="statusOptions"
        empty-value-to="undefined"
        variant="ghost"
      />
      <Select
        v-model="targetEmployeeId"
        :placeholder="t('employee')"
        :options="employeeOptions"
        variant="ghost"
      />
    </div>

    <div class="space-y-4">
      <template v-for="entry in reviews" :key="entry.id">
        <ReviewListItem :entry="entry" />
      </template>
    </div>

    <div
      v-if="!isPending && !reviews.length"
      class="rounded-lg border border-obsidian-200 bg-white px-4 py-6 text-center"
    >
      <span class="text-sm text-obsidian-600">{{ t('noResults') }}</span>
    </div>

    <div v-if="hasNextPage" class="mt-4 flex justify-center">
      <CVButton outline :is-loading="isFetchingNextPage" @click="fetchNextPage">
        {{ t('loadMore') }}
      </CVButton>
    </div>
  </section>
</template>

<script setup lang="ts">
import { reviewService } from '@/api/reviews.service';
import DateRangeFilterPicker from '@/components/DateRangeFilterPicker.vue';
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import Select from '@/components/Select.vue';
import { useTokenPayload } from '@/queries/auth/use-login';
import { isJWTUser } from '@/entities/auth/jwt-user.entity';
import { useUsersWithQuery } from '@/queries/use-users';
import { useUiStore } from '@/stores/ui.store';
import { FindAllReviewsSort, Order, ReviewStatus } from '@carvia/ros-client-types';
import { DateTime } from 'luxon';
import { computed, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { useInfiniteQuery } from '@tanstack/vue-query';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { useRouteQueryRef } from '@/hooks/use-route-query-ref';
import CVButton from '@/components/buttons/CVButton.vue';
import { UserRole } from '@/entities/auth/user-role.enum';
import { useAbility } from '@casl/vue';
import ReviewListItem from './components/ReviewListItem.vue';

const { t } = useI18n();
const uiStore = useUiStore();
const ability = useAbility();

watchEffect(() => {
  uiStore.setHeaderTitle(t('title'));
});

const getCurrentPayoutPeriodStartDate = (date: Date = new Date()) => {
  return DateTime.fromJSDate(date)
    .minus({ months: date.getDate() < 15 ? 1 : 0 })
    .startOf('day')
    .set({ day: 15 })
    .toJSDate();
};

const { data: tokenPayload } = useTokenPayload();

const startDate = useRouteQueryRef(
  'startDate',
  getCurrentPayoutPeriodStartDate() as Date | null,
  'date',
);
const endDate = useRouteQueryRef(
  'endDate',
  DateTime.fromJSDate(getCurrentPayoutPeriodStartDate())
    .plus({ months: 1 })
    .toJSDate() as Date | null,
  'date',
);
const status = useRouteQueryRef<ReviewStatus | undefined>(
  'status',
  ReviewStatus.COMPLETED,
  'string',
);
const targetEmployeeId = useRouteQueryRef(
  'targetEmployeeId',
  tokenPayload.value?.id as string | undefined,
  'string',
);

const statusOptions = [
  { label: t('all'), value: null },
  { label: t('pending'), value: ReviewStatus.PENDING },
  { label: t('completed'), value: ReviewStatus.COMPLETED },
];

const { data: employeesData } = useUsersWithQuery({
  role: [UserRole.HUB_MANAGER, UserRole.HUB_AGENT],
  stationIds: (
    ability.relevantRuleFor('operateOn', 'Station')?.conditions?.['id'] as
      | { $in: string[] }
      | undefined
  )?.$in,
  limit: 500,
});
const employees = useFlattenPaginatedData(employeesData);

const targetEmployeeIdFilter = computed(
  () =>
    (
      ability.relevantRuleFor('list', 'Review')?.conditions?.targetEmployeeId as
        | { $eq: string }
        | undefined
    )?.$eq,
);

const employeeOptions = computed(() => {
  const options = (employees.value ?? [])
    .filter(
      (employee) => !targetEmployeeIdFilter.value || targetEmployeeIdFilter.value === employee.id,
    )
    .map((u) => ({
      label: `${u.firstName} ${u.lastName}`,
      value: u.id,
    }));
  const payload = tokenPayload.value;
  if (isJWTUser(payload) && payload.id && !options.some(({ value }) => value === payload.id)) {
    options.push({
      label: `${payload.firstName} ${payload.lastName}`,
      value: payload.id,
    });
  }
  return options;
});

const queryKey = computed(() => [
  'reviews',
  {
    targetEmployeeIds: targetEmployeeId.value,
    startDate: startDate.value?.toISOString() ?? null,
    endDate: endDate.value?.toISOString() ?? null,
    status: status.value ?? null,
  },
]);

const { data, isPending, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
  queryKey,
  queryFn: async ({ pageParam = 1 }) =>
    reviewService.getReviews({
      page: pageParam,
      limit: 20,
      ...(targetEmployeeId.value ? { targetEmployeeIds: [targetEmployeeId.value] } : {}),
      ...(status.value ? { status: [status.value] } : {}),
      ...(startDate.value ? { startDate: startDate.value } : {}),
      ...(endDate.value ? { endDate: endDate.value } : {}),
      order: Order.DESC,
      sort: [FindAllReviewsSort.CREATED_DATE],
    }),
  getNextPageParam: (lastPage) => lastPage.meta.nextPage,
  initialPageParam: 1,
});

const reviews = useFlattenPaginatedData(data);
</script>

<i18n lang="json">
{
  "en": {
    "title": "Customer Ratings",
    "dateRange": "Date range",
    "employeePlaceholder": "Employee",
    "employee": "Employee",
    "allEmployees": "All employees",
    "all": "All",
    "pending": "Pending",
    "completed": "Completed",
    "loadMore": "Load more",
    "date": "Date",
    "booking": "Booking",
    "customer": "Customer",
    "statusLabel": "Status",
    "status": {
      "PENDING": "Pending",
      "COMPLETED": "Completed"
    },
    "rating": "Rating",
    "comment": "Comment",
    "noResults": "No reviews found for this time range"
  },
  "de": {
    "title": "Kundenbewertungen",
    "dateRange": "Zeitraum",
    "employeePlaceholder": "Mitarbeiter",
    "employee": "Mitarbeiter",
    "allEmployees": "Alle Mitarbeiter",
    "all": "Alle",
    "pending": "Offen",
    "completed": "Abgeschlossen",
    "loadMore": "Mehr laden",
    "date": "Datum",
    "booking": "Buchung",
    "customer": "Kunde",
    "statusLabel": "Status",
    "status": {
      "PENDING": "Offen",
      "COMPLETED": "Abgeschlossen"
    },
    "rating": "Bewertung",
    "comment": "Kommentar",
    "noResults": "Keine Bewertungen in diesem Zeitraum gefunden"
  },
  "es": {
    "title": "Valoraciones de clientes",
    "dateRange": "Rango de fechas",
    "employeePlaceholder": "Empleado",
    "employee": "Empleado",
    "allEmployees": "Todos los empleados",
    "all": "Todas",
    "pending": "Pendientes",
    "completed": "Completadas",
    "loadMore": "Cargar más",
    "date": "Fecha",
    "booking": "Reserva",
    "customer": "Cliente",
    "statusLabel": "Estado",
    "status": {
      "PENDING": "Pendiente",
      "COMPLETED": "Completada"
    },
    "rating": "Valoración",
    "comment": "Comentario",
    "noResults": "No hay valoraciones en este periodo"
  }
}
</i18n>
